import React, { useState, useEffect } from 'react';
import { Counsellor } from '../../types/counsellor';
import { Box, Grid, Typography, Link, Alert, Checkbox, FormControlLabel } from '@mui/material';
import { refreshDocumentUrls } from '../../utils/api';

interface DocumentsInformationProps {
  counsellor: Counsellor;
  isAdmin?: boolean;
  onValidationOverride?: (override: boolean) => void;
}

const DocumentsInformation: React.FC<DocumentsInformationProps> = ({ counsellor, isAdmin = false, onValidationOverride }) => {
  const [documentUrls, setDocumentUrls] = useState<{
    cv?: string;
    insuranceCertificate?: string;
    dbsCertificate?: string;
    onlineLearningCertificate?: string;
    visaDocument?: string;
    workPermitDocument?: string;
    contractDocument?: string;
  }>({
    cv: '',
    insuranceCertificate: '',
    dbsCertificate: '',
    onlineLearningCertificate: '',
    visaDocument: '',
    workPermitDocument: '',
    contractDocument: '',
  });
  const [, setError] = useState<string | null>(null);
  const [validationOverride, setValidationOverride] = useState<boolean>(false);
  const [missingDocuments, setMissingDocuments] = useState<string[]>([]);

  useEffect(() => {
    // Check for missing required documents
    const checkMissingDocuments = () => {
      const missing: string[] = [];
      
      if (!counsellor.application?.legal?.insuranceCertificate?.url) {
        missing.push('Insurance Certificate');
      }
      
      // Only require DBS Certificate if they're using certificate type (not update service)
      if (!counsellor.application?.legal?.dbsCertificate?.url && 
          counsellor.application?.legal?.dbsCheck &&
          counsellor.application?.legal?.dbsType === 'certificate') {
        missing.push('DBS Certificate');
      }
      
      setMissingDocuments(missing);
    };
    
    checkMissingDocuments();
    
    const fetchDocuments = async () => {
      try {
        // Use the counsellor ID to refresh all document URLs at once
        const refreshedUrls = await refreshDocumentUrls(counsellor._id);
        setDocumentUrls(refreshedUrls);
      } catch (error) {
        console.error('Error fetching document URLs:', error);
        setError('Failed to load document URLs');
      }
    };

    fetchDocuments();
  }, [counsellor]);

  const handleDocumentClick = async (e: React.MouseEvent<HTMLElement>, documentType: keyof typeof documentUrls) => {
    e.preventDefault();
    
    try {
      // Determine which document URL to refresh based on the document type
      switch (documentType) {
        case 'cv':
        case 'onlineLearningCertificate':
        case 'insuranceCertificate':
        case 'dbsCertificate':
        case 'contractDocument':
          // Refresh all document URLs using counsellor ID
          const refreshedUrls = await refreshDocumentUrls(counsellor._id);
          setDocumentUrls(refreshedUrls);
          
          // Open the specific document if URL is available
          const documentUrl = refreshedUrls[documentType];
          if (documentUrl) {
            window.open(documentUrl, '_blank');
          }
          break;
        case 'workPermitDocument':
          // Currently there is no dedicated work permit document field in the LegalInfo interface
          // This case is kept for future implementation when such a field is added
          console.log('Work permit document viewing not implemented yet');
          break;
        default:
          break;
      }
      
      // Document opening is now handled in each case
    } catch (error) {
      console.error(`Error opening ${documentType} document:`, error);
      setError(`Failed to open ${documentType} document`);
    }
  };

  return (
    <Box sx={{ width: '100%', mb: 4 }}>
      <div className="glass-card p-6 rounded-lg">
        <Typography variant="h6" className="theme-text-primary" gutterBottom>
          Documents
        </Typography>

        <Grid container spacing={3}>
          {/* CV Document */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" fontWeight="bold" className="theme-text-primary">CV:</Typography>
            {counsellor.application?.counselling?.cv ? (
              <Link 
                component="button"
                onClick={(e) => handleDocumentClick(e as any, 'cv')}
                sx={{ cursor: 'pointer', color: 'rgba(255, 255, 255, 0.9)', textDecoration: 'underline' }}
              >
                {counsellor.application.counselling.cv.originalName || 'CV Document'}
              </Link>
            ) : (
              <Typography className="theme-text-secondary">Not provided</Typography>
            )}
          </Grid>

          {/* Telephone Certificate */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" fontWeight="bold" className="theme-text-primary">Telephone Certificate:</Typography>
            {counsellor.application?.legal?.onlineLearningCertificate ? (
              <Link 
                component="button"
                onClick={(e) => handleDocumentClick(e as any, 'onlineLearningCertificate')}
                sx={{ cursor: 'pointer', color: 'rgba(255, 255, 255, 0.9)', textDecoration: 'underline' }}
              >
                {(counsellor.application.legal.onlineLearningCertificate as any).originalName || counsellor.application.legal.onlineLearningCertificate.fileName || 'Telephone Certificate'}
              </Link>
            ) : (
              <Typography className="theme-text-secondary">Not provided (Optional)</Typography>
            )}
          </Grid>

          {/* Insurance Document */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" fontWeight="bold" className="theme-text-primary">Insurance Certificate:</Typography>
            {counsellor.application?.legal?.insuranceCertificate ? (
              <Link 
                component="button"
                onClick={(e) => handleDocumentClick(e as any, 'insuranceCertificate')}
                sx={{ cursor: 'pointer', color: 'rgba(255, 255, 255, 0.9)', textDecoration: 'underline' }}
              >
                {counsellor.application.legal.insuranceCertificate.originalName || 'Insurance Certificate'}
              </Link>
            ) : (
              <Typography className="theme-text-secondary">Not provided</Typography>
            )}
          </Grid>

          {/* DBS Document */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" fontWeight="bold" className="theme-text-primary">DBS Certificate:</Typography>
            {counsellor.application?.legal?.dbsCertificate ? (
              <Link 
                component="button"
                onClick={(e) => handleDocumentClick(e as any, 'dbsCertificate')}
                sx={{ cursor: 'pointer', color: 'rgba(255, 255, 255, 0.9)', textDecoration: 'underline' }}
              >
                {counsellor.application.legal.dbsCertificate.originalName || 'DBS Certificate'}
              </Link>
            ) : (
              <Typography className="theme-text-secondary">Not provided</Typography>
            )}
          </Grid>

          {/* Contract Document */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" fontWeight="bold" className="theme-text-primary">Placement Agreement:</Typography>
            {counsellor.contractDocument ? (
              <Link 
                component="button"
                onClick={(e) => handleDocumentClick(e as any, 'contractDocument')}
                sx={{ cursor: 'pointer', color: 'rgba(255, 255, 255, 0.9)', textDecoration: 'underline' }}
              >
                {counsellor.contractDocument.originalName || 'Placement Agreement'}
              </Link>
            ) : counsellor.contractSigned ? (
              <Typography className="theme-text-secondary">Signed on {new Date(counsellor.contractSignedDate || '').toLocaleDateString('en-GB')} (Document pending)</Typography>
            ) : (
              <Typography className="theme-text-secondary">Not signed</Typography>
            )}
          </Grid>
          
          {/* Work Permit Information (if applicable) */}
          {counsellor.application?.legal?.workPermit && (
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" fontWeight="bold" className="theme-text-primary">Work Permit:</Typography>
              {counsellor.application?.legal?.workPermitDetails ? (
                <Box>
                  <Typography className="theme-text-primary">
                    {counsellor.application.legal.workPermitDetails}
                    {counsellor.application.legal.workPermitNoExpiry ? (
                      <> (No expiry date - indefinite)</>
                    ) : counsellor.application.legal.workPermitExpiryDate && (
                      <> (Expires: {new Date(counsellor.application.legal.workPermitExpiryDate).toLocaleDateString()})</>
                    )}
                  </Typography>
                </Box>
              ) : (
                <Typography className="theme-text-secondary">Details not provided</Typography>
              )}
            </Grid>
          )}
          
          {missingDocuments.length > 0 && (
            <Grid item xs={12}>
              <Alert severity="warning" sx={{ mt: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold">Missing Required Documents:</Typography>
                <ul>
                  {missingDocuments.map((doc, index) => (
                    <li key={index}>{doc}</li>
                  ))}
                </ul>
                {isAdmin && onValidationOverride && (
                  <Box sx={{ mt: 2 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={validationOverride}
                          onChange={(e) => {
                            setValidationOverride(e.target.checked);
                            onValidationOverride(e.target.checked);
                          }}
                        />
                      }
                      label="Override validation (Admin only)"
                    />
                    {validationOverride && (
                      <Typography color="error" variant="body2">
                        Warning: Overriding document validation will allow onboarding completion without all required documents.
                      </Typography>
                    )}
                  </Box>
                )}
              </Alert>
            </Grid>
          )}
          
          <Grid item xs={12}>
            <Typography variant="body2" className="theme-text-secondary" sx={{ mt: 2 }}>
              Note: These documents are used for legal verification and are reviewed by administrators during the onboarding process.
            </Typography>
          </Grid>
        </Grid>
      </div>
    </Box>
  );
};

export default DocumentsInformation;
